import { useCallback, useRef } from "react";

/**
 * Defers an action until a Radix dropdown menu has fully closed.
 *
 * Opening a dialog straight from a menu item races the menu's exit: the menu
 * stays mounted through its close animation and refocuses its own content when
 * the pointer leaves the item (which happens as soon as the dialog overlay
 * appears), so the dialog loses focus to an element that then unmounts.
 * `onCloseAutoFocus` fires once the menu is really gone; scheduling the action
 * there keeps the dialog's initial focus intact.
 *
 * Wire `onCloseAutoFocus` onto the `DropdownMenuContent` and call `schedule`
 * from the item handler. Menus closed without a scheduled action keep Radix's
 * default focus restore.
 */
export const useAfterMenuClose = () => {
  const pendingRef = useRef<(() => void) | null>(null);

  const schedule = useCallback((action: () => void) => {
    pendingRef.current = action;
  }, []);

  const onCloseAutoFocus = useCallback((event: Event) => {
    const action = pendingRef.current;
    if (!action) return;
    pendingRef.current = null;
    event.preventDefault();
    action();
  }, []);

  return { schedule, onCloseAutoFocus };
};
